import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/pro-light-svg-icons/faArrowsRotate';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedPresaveWidget } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../../common/types';
import { useOpenSongPage } from '../../catalog/song/page/useOpenSongPage';
import { formatReleaseDay } from '../blocks/describeCatalogLinks';
import { BlockAutosave } from '../forms/useBlockAutosave';
import { catalogLinksMessages as m } from './catalogLinksMessages';
import { CatalogLinksSongBar } from './CatalogLinksSongBar';
import { CatalogLinksTimeline } from './CatalogLinksTimeline';
import { CatalogLinksPresaveSwitch } from './CatalogLinksPresaveSwitch';
import { CatalogLinksPresaveServices } from './CatalogLinksPresaveServices';
import { CatalogLinksFoundServices } from './CatalogLinksFoundServices';
import { withBrandWord } from '../../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './CatalogLinks.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CatalogLinksFormProps {
  widget: SerializedPresaveWidget;
  accountId: number;
  smartLinkId: number;
  autosave: BlockAutosave;
}

/**
 * The Catalog links block (type `presave`, design c1, c1b, c2, c7): the song, the timeline, the
 * pre-save and the services. Before the release the services are the pre-save buttons; after it,
 * the links that Music24 found. The links update by themselves.
 */
const CatalogLinksFormComponent: React.FC<CatalogLinksFormProps> = ({ widget, accountId, smartLinkId, autosave }) => {
  const intl = useIntl();
  const openSongPage = useOpenSongPage();
  const { data } = widget;
  const [services, setServices] = useState<string[]>(data.services);
  const { save } = autosave;
  const hasDate = data.releaseDate !== '' && !Number.isNaN(Date.parse(data.releaseDate));
  const hasCampaign = data.hasCampaign !== false && data.campaignUuid !== '';
  const songUuid = typeof data.catalogItemUuid === 'string' ? data.catalogItemUuid : null;
  const links = data.resolvedUrls ?? {};

  const onServices = useCallback(
    (next: string[]) => {
      setServices(next);
      save(
        songUuid === null
          ? { campaignUuid: data.campaignUuid, services: next }
          : { campaignUuid: hasCampaign ? data.campaignUuid : undefined, catalogItemUuid: songUuid, services: next }
      );
    },
    [data.campaignUuid, hasCampaign, save, songUuid]
  );
  const openSong = useCallback(() => {
    if (songUuid !== null) {
      openSongPage(songUuid, 'release');
    }
  }, [openSongPage, songUuid]);

  if (!hasCampaign && songUuid === null) {
    return (
      <p className={clsx(styles.note, styles.noteWarn)} data-testid={`form-${SmartLinkWidgetType.presave}`}>
        {intl.formatMessage(m.songGone)}
      </p>
    );
  }

  return (
    <div className={styles.form} data-testid={`form-${SmartLinkWidgetType.presave}`}>
      <CatalogLinksSongBar widget={widget} />
      {data.released && Object.keys(links).length === 0 && (
        <div className={styles.note}>
          <span>{intl.formatMessage(m.noLinks, { title: data.trackTitle })}</span>
          {songUuid !== null && (
            <button type="button" className={styles.change} onClick={openSong}>
              {intl.formatMessage(m.addLinkOnSongPage)}
            </button>
          )}
        </div>
      )}
      {data.released ? (
        <CatalogLinksFoundServices services={services} links={links} />
      ) : (
        <>
          {hasDate && <CatalogLinksTimeline day={formatReleaseDay(intl, data.releaseDate)} hasPresave={hasCampaign} />}
          {data.closed ? (
            <p className={clsx(styles.note, styles.noteWarn)}>
              {intl.formatMessage(m.closed, { title: data.trackTitle })}
            </p>
          ) : (
            <>
              {hasDate ? (
                <CatalogLinksPresaveSwitch
                  accountId={accountId}
                  smartLinkId={smartLinkId}
                  widget={widget}
                  autosave={autosave}
                />
              ) : (
                <p className={styles.help}>{intl.formatMessage(m.presaveNeedsDate)}</p>
              )}
              {hasCampaign && (
                <CatalogLinksPresaveServices services={services} disabled={false} onChange={onServices} />
              )}
            </>
          )}
        </>
      )}
      <p className={styles.note}>
        <FontAwesomeIcon icon={faArrowsRotate} className={styles.noteIcon} />
        {withBrandWord(
          intl.formatMessage(data.released ? m.autoNoteAfter : m.autoNoteBefore, { title: data.trackTitle })
        )}
      </p>
      {data.released && data.presaveCount !== undefined && data.presaveCount > 0 && (
        <p className={styles.help}>
          {intl.formatMessage(m.fans, { count: data.presaveCount, title: data.trackTitle })}
        </p>
      )}
    </div>
  );
};

export const CatalogLinksForm = TypedMemo(CatalogLinksFormComponent);
